import { computed } from 'vue';
import { roundMoney } from '@/composables/usePaymentMethodCalculations';

function toDateKey(ms) {
  const d = new Date(ms);
  const pad = (n) => (n < 10 ? '0' : '') + n;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toMonthKey(ms) {
  const d = new Date(ms);
  const pad = (n) => (n < 10 ? '0' : '') + n;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

function formatDateDdMmYy(dateKey) {
  if (!dateKey) return '';
  const [y, m, d] = dateKey.split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

function endOfMonthMs(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, m, 0, 23, 59, 59, 999).getTime();
}

function endOfDayMs(dateKey) {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

function monthKeysInRange(minMs, maxMs) {
  const keys = [];
  const cursor = new Date(minMs);
  cursor.setDate(1);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(maxMs);
  while (cursor <= end) {
    keys.push(toMonthKey(cursor.getTime()));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return keys;
}

function dayKeysInRange(minMs, maxMs) {
  const keys = [];
  const cursor = new Date(minMs);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(maxMs);
  end.setHours(23, 59, 59, 999);
  while (cursor <= end) {
    keys.push(toDateKey(cursor.getTime()));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

export function useBankBalances(bankAccounts, bankTransactions) {
  const activeAccounts = computed(() =>
    bankAccounts.value.filter((a) => a.isActive !== false)
  );

  function transactionsForAccount(accountId, includeBeforeOpening = false) {
    const account = bankAccounts.value.find((a) => a.id === accountId);
    const openingDate = account?.openingBalanceDate || 0;
    return bankTransactions.value.filter((tx) => {
      if (tx.accountId !== accountId) return false;
      if (includeBeforeOpening) return true;
      return (Number(tx.date) || 0) >= openingDate;
    });
  }

  function accountBalance(accountId) {
    const account = bankAccounts.value.find((a) => a.id === accountId);
    if (!account) return 0;
    const opening = roundMoney(Number(account.openingBalance) || 0);
    const sum = transactionsForAccount(accountId).reduce(
      (acc, tx) => acc + (Number(tx.amount) || 0),
      0
    );
    return roundMoney(opening + sum);
  }

  const consolidatedBalance = computed(() =>
    roundMoney(activeAccounts.value.reduce((acc, account) => acc + accountBalance(account.id), 0))
  );

  function filterByDateRange(transactions, minMs, maxMs) {
    return transactions.filter((tx) => {
      const date = Number(tx.date) || 0;
      return date >= minMs && date <= maxMs;
    });
  }

  function summarizeTransactions(transactions) {
    let inflows = 0;
    let outflows = 0;
    for (const tx of transactions) {
      const amount = Number(tx.amount) || 0;
      if (amount > 0) inflows += amount;
      else if (amount < 0) outflows += Math.abs(amount);
    }
    return {
      inflows: roundMoney(inflows),
      outflows: roundMoney(outflows),
      net: roundMoney(inflows - outflows),
    };
  }

  function aggregateByDay(transactions) {
    const byDay = {};
    for (const tx of transactions) {
      const key = toDateKey(Number(tx.date) || 0);
      if (!byDay[key]) {
        byDay[key] = { dateKey: key, inflows: 0, outflows: 0, net: 0 };
      }
      const amount = Number(tx.amount) || 0;
      if (amount > 0) byDay[key].inflows += amount;
      else if (amount < 0) byDay[key].outflows += Math.abs(amount);
      byDay[key].net = byDay[key].inflows - byDay[key].outflows;
    }
    return Object.values(byDay)
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey))
      .map((row) => ({
        ...row,
        inflows: roundMoney(row.inflows),
        outflows: roundMoney(row.outflows),
        net: roundMoney(row.net),
        dateLabel: formatDateDdMmYy(row.dateKey),
      }));
  }

  function aggregateByMonth(transactions) {
    const byMonth = {};
    for (const tx of transactions) {
      const key = toMonthKey(Number(tx.date) || 0);
      if (!byMonth[key]) {
        byMonth[key] = { monthKey: key, inflows: 0, outflows: 0, net: 0 };
      }
      const amount = Number(tx.amount) || 0;
      if (amount > 0) byMonth[key].inflows += amount;
      else if (amount < 0) byMonth[key].outflows += Math.abs(amount);
      byMonth[key].net = byMonth[key].inflows - byMonth[key].outflows;
    }
    return Object.values(byMonth)
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
      .map((row) => ({
        ...row,
        inflows: roundMoney(row.inflows),
        outflows: roundMoney(row.outflows),
        net: roundMoney(row.net),
        monthLabel: formatMonthLabel(row.monthKey),
      }));
  }

  function formatMonthLabel(monthKey) {
    const [y, m] = monthKey.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${months[Number(m) - 1]}/${y}`;
  }

  /**
   * Balance at a point in time for one or all accounts.
   * Each account contributes opening balance + transactions on/after its opening date and on/before asOfMs.
   */
  function balanceAt(asOfMs, accountId = null) {
    const accounts = accountId
      ? activeAccounts.value.filter((a) => a.id === accountId)
      : activeAccounts.value;

    let total = 0;
    for (const account of accounts) {
      const openingDate = Number(account.openingBalanceDate) || 0;
      if (asOfMs < openingDate) continue;
      let balance = roundMoney(Number(account.openingBalance) || 0);
      for (const tx of bankTransactions.value) {
        if (tx.accountId !== account.id) continue;
        const txDate = Number(tx.date) || 0;
        if (txDate >= openingDate && txDate <= asOfMs) {
          balance = roundMoney(balance + (Number(tx.amount) || 0));
        }
      }
      total = roundMoney(total + balance);
    }
    return total;
  }

  /**
   * Running balance at the end of each day/month within a period.
   */
  function runningBalanceTimeline(mode, minMs, maxMs, accountId = null) {
    if (!minMs || !maxMs || minMs > maxMs) return [];

    const keys = mode === 'month'
      ? monthKeysInRange(minMs, maxMs)
      : dayKeysInRange(minMs, maxMs);

    return keys.map((key) => {
      const asOfMs = mode === 'month' ? endOfMonthMs(key) : endOfDayMs(key);
      return {
        key,
        label: mode === 'month' ? formatMonthLabel(key) : formatDateDdMmYy(key),
        balance: balanceAt(asOfMs, accountId),
      };
    });
  }

  function consolidatedRunningBalance(mode, minMs, maxMs) {
    return runningBalanceTimeline(mode, minMs, maxMs, null);
  }

  function accountRunningBalance(mode, minMs, maxMs, accountId) {
    return runningBalanceTimeline(mode, minMs, maxMs, accountId);
  }

  return {
    accountBalance,
    consolidatedBalance,
    transactionsForAccount,
    filterByDateRange,
    summarizeTransactions,
    aggregateByDay,
    aggregateByMonth,
    balanceAt,
    runningBalanceTimeline,
    consolidatedRunningBalance,
    accountRunningBalance,
    toDateKey,
    formatDateDdMmYy,
    formatMonthLabel,
  };
}
